import React from 'react';

import type { AgencyConfigPayload } from '../../services/agencyConfig';
import type { AgencyConfigUpdate } from '../../services/api/types.gen';
import { useAgencyConfigSection } from '../../hooks/useAgencyConfigSection';
import { SettingDateValue, SettingsEditFooter } from './SettingsSectionParts';

export const GO_LIVE_LABEL = 'Go-live date';

type Values = { goLive: string | null; canEdit: boolean };

const DEFAULTS: Values = { goLive: null, canEdit: false };

function fromConfig(cfg: AgencyConfigPayload): Values {
  return {
    goLive: cfg.go_live_date ?? null,
    canEdit: cfg.can_edit_onboarding_dates ?? false,
  };
}

function diff(values: Values, saved: Values): AgencyConfigUpdate {
  return values.goLive !== saved.goLive ? { go_live_date: values.goLive } : {};
}

/**
 * The day the agency starts working in Roger. The Roger team or a chart
 * workflow editor sets it.
 */
const OnboardingDatesSection: React.FC = () => {
  const section = useAgencyConfigSection({
    enabled: true,
    initial: DEFAULTS,
    fromConfig,
    diff,
    successMessage: 'Go-live date updated',
    errorMessage: 'Failed to update the go-live date',
  });
  const { values, setValues, isLoading, loadFailed } = section;
  const editing = section.isEditing && values.canEdit;

  return (
    <section className='space-y-4' aria-labelledby='onboarding-dates-heading'>
      <div className='space-y-1'>
        <h3 id='onboarding-dates-heading' className='text-base font-semibold'>
          Onboarding dates
        </h3>
        <p className='text-sm text-muted-foreground'>
          Set by the Roger team or a chart workflow editor
        </p>
      </div>

      <div className='flex flex-col gap-3 rounded-md border p-3 md:flex-row md:items-center md:justify-between md:gap-4'>
        <label htmlFor='onboarding-go-live' className='text-sm font-medium'>
          {GO_LIVE_LABEL}
        </label>
        <SettingDateValue
          id='onboarding-go-live'
          label={GO_LIVE_LABEL}
          value={values.goLive}
          editing={editing}
          isLoading={isLoading}
          loadFailed={loadFailed}
          onChange={goLive => setValues(prev => ({ ...prev, goLive }))}
        />
      </div>

      <SettingsEditFooter
        canEdit={values.canEdit}
        isEditing={section.isEditing}
        isSaving={section.isSaving}
        editDisabled={isLoading || loadFailed}
        onEdit={section.startEditing}
        onCancel={section.cancel}
        onSave={() => void section.save()}
      />
    </section>
  );
};

export default OnboardingDatesSection;
